import { ApiError } from '@gopinhos/shared/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { WalkingGoat } from '../components/illustrations/GoatMascot';
import { WorkflowHero } from '../components/illustrations/WorkflowHero';
import { LanguageSwitcher } from '../components/ui/language-switcher';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../lib/auth-context';

export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login({ email, password, totp: needsTotp ? totp : undefined });
      navigate('/dashboard');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.problem.title === 'mfa code required') {
          setNeedsTotp(true);
          setError(t('login.mfaPrompt'));
        } else {
          setError(err.problem.title || t('login.loginFailed'));
        }
      } else {
        setError(t('login.serverError'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-full bg-surface-50 app-bg overflow-x-hidden pb-44">
      <div className="absolute right-4 top-4 z-10">
        <LanguageSwitcher />
      </div>

      {/* Left visual panel — only shown on md+ */}
      <aside className="hidden md:flex md:w-1/2 lg:w-3/5 flex-col justify-between bg-gradient-to-br from-brand-50 via-white to-surface-100 px-10 py-12">
        <div>
          <span className="text-xl font-bold tracking-tight text-brand-700">
            GOPINHOS
          </span>
          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-surface-500">
            Gestão de leads & prospecção
          </p>
        </div>
        <div className="flex justify-center">
          <WorkflowHero
            className="w-full max-w-[480px]"
            ariaLabel="Cards de pipeline conectados representando o fluxo de leads"
          />
        </div>
        <div className="space-y-1.5">
          <p className="text-lg font-semibold text-surface-800">
            Do primeiro contato à liquidação, em um só fluxo.
          </p>
          <p className="text-sm text-surface-600 max-w-md">
            Pipelines configuráveis, SLAs por estágio, notas compartilhadas e
            rastreamento auditável de cada movimento.
          </p>
        </div>
      </aside>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md shadow-card">
          <CardHeader>
            <div className="md:hidden mb-2 flex items-center gap-2">
              <span className="text-lg font-bold tracking-tight text-brand-700">
                GOPINHOS
              </span>
              <span className="inline-flex items-center rounded-full bg-accent-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-700">
                beta
              </span>
            </div>
            <CardTitle className="text-xl">{t('login.title')}</CardTitle>
            <p className="text-sm text-surface-500">{t('login.subtitle')}</p>
          </CardHeader>
          <CardContent>
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {needsTotp && (
              <div className="space-y-1.5">
                <Label htmlFor="totp">{t('login.totp')}</Label>
                <Input
                  id="totp"
                  inputMode="numeric"
                  pattern="\\d{6}"
                  maxLength={6}
                  required
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                />
              </div>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="submit" className="w-full" size="lg" disabled={submitting}>
              {submitting ? t('common.signingIn') : t('common.signIn')}
            </Button>
            <p className="text-center text-xs text-surface-500">{t('login.forgotPassword')}</p>
          </form>
        </CardContent>
        </Card>
      </div>

      {/* Trio de cabras passeando pelo rodapé — tamanhos diferentes dão
          uma sensação de profundidade (líder na frente, "filhotinhas" atrás). */}
      <WalkingGoat durationSeconds={32} />
    </div>
  );
}
